import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import type { AnnotationsListParams } from "@similarweb/pro-api-client";
import { serverResponseFormat } from "@similarweb/pro-logic-components";

import type { IUser } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import { useApiQuery } from "app/modules/api-client";
import { useUserNames } from "app/pages/digital-marketing/monitor-and-protect/services/api/users";
import type { IAccountUser } from "app/sharing/SharingService";
import type { DatesRange } from "app/pages/digital-marketing/brand-monitoring-campaigns/reports/ads-report/shared/BrandMonitoringAdsReportUtils";
import durationService from "app/services/DurationService";

import type { IDeserializeAnnotationOptions } from "./chartAnnotationsApi";
import { deserializeAnnotation } from "./chartAnnotationsApi";

export interface IUseAnnotationsQueryOptions {
    enabled?: boolean;
    deserializeOptions?: IDeserializeAnnotationOptions;
}

export function useAnnotationsQuery(tags: string[], options?: IUseAnnotationsQueryOptions) {
    const { enabled = true, deserializeOptions } = options || {};
    const {
        params: { duration },
    }: { params: { duration: string } } = useCurrentStateAndParams();

    const durationObject: DatesRange = useMemo(
        () => durationService.getDurationObjectForCampaigns(duration),
        [duration],
    );

    const { data: users = {} } = useUserNames();
    const extendedUsersObject = useMemo(() => {
        const currentUser = swSettings.user as Pick<IUser, "id" | "username" | "firstname" | "lastname">;
        const currentUserToAccountUser: IAccountUser = {
            Id: currentUser.id,
            Email: currentUser.username,
            FirstName: currentUser.firstname,
            LastName: currentUser.lastname,
            User: currentUser.firstname + " " + currentUser.lastname,
        };

        return { ...users, [currentUserToAccountUser.Id]: currentUserToAccountUser };
    }, [users]);

    const dates = useMemo(
        () => ({
            from: durationObject.from.format(serverResponseFormat),
            to: durationObject.to.format(serverResponseFormat),
        }),
        [durationObject],
    );

    const { data: annotations = [], ...rest } = useApiQuery(
        {
            tag: "Annotations",
            methodName: "annotationsList",
            options: {
                enabled,
                select: (data) => {
                    if (!data) {
                        return [];
                    }

                    return data.toSorted((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
                },
            },
        },
        {
            from: dates.from,
            to: dates.to,
            tags: tags,
        } satisfies AnnotationsListParams,
    );

    return {
        data: annotations.map((annotation) =>
            deserializeAnnotation(annotation, {
                userList: extendedUsersObject,
                ...deserializeOptions,
            }),
        ),
        ...rest,
    };
}
